import { useCallback, useRef } from 'react';
import {
  AutocompleteArrayInput,
  AutocompleteArrayInputProps,
  useGetList,
  useNotify,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';

import { B2bCampaignType } from '@Common/types/bo/b2b/campaigns';
import { B2bContractsQuery, GetB2bContractsOneResponse } from '@Common/types/bo/b2b/contracts';
import { isInvalidVoluntaryDepositContract } from '@Common/validators/contracts';

import { useValueChanged } from '@sh4res/bo-common/hooks';

type B2bContractArraySelectInputProps = Omit<
  AutocompleteArrayInputProps<GetB2bContractsOneResponse>,
  'filter' | 'data-testid'
> & {
  source: string;
  reference?: 'b2b/contracts' | 'contracts';
  filter: B2bContractsQuery['filter'];
  campaignType?: B2bCampaignType | null;
  optionText?: (record: GetB2bContractsOneResponse) => string;
  testId?: string;
};

const optionTextDefault = (option: GetB2bContractsOneResponse) =>
  `(${option.id}) ${option.contract_number}`;

const matchSuggestion = (filter: string, choice: GetB2bContractsOneResponse) => {
  const lower = filter.toLowerCase();
  return (
    choice.contract_number?.toLowerCase().includes(lower) || String(choice.id).includes(filter)
  );
};

const CAMPAIGN_PROCESSING_KEYS = [
  'payout_by',
  'choice_collected_by',
  'allocation_by',
  'matching_contribution_computed_by',
] as const;

export const B2bContractArraySelectInput = ({
  source = 'b2b_contract_ids',
  reference = 'b2b/contracts',
  optionText = optionTextDefault,
  isRequired,
  filter,
  campaignType = null,
  validate,
  testId,
  ...props
}: B2bContractArraySelectInputProps) => {
  const { data: choices = [], isLoading } = useGetList(reference, {
    filter: { ...filter, not_status: 'terminated' },
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'id', order: 'DESC' },
  });

  const { getValues, setValue } = useFormContext();
  const notify = useNotify();
  const selectedIds: number[] = useWatch({ name: source }) ?? [];
  const previousSelectedIdsRef = useRef<number[]>(selectedIds);

  useValueChanged(selectedIds, () => {
    if (selectedIds.length === 0) {
      previousSelectedIdsRef.current = [];
    }
  });

  const handleChange = useCallback(
    (newSelectedIds: number[]) => {
      const previousSelectedIds = previousSelectedIdsRef.current;
      const newlyAddedIds = newSelectedIds.filter((id) => !previousSelectedIds.includes(id));

      if (newlyAddedIds.length === 0) {
        previousSelectedIdsRef.current = newSelectedIds;
        return;
      }

      const firstContract = choices.find((entry) => entry.id === previousSelectedIds[0]);
      let errorMessage: string | null = null;
      const rejectedIds: number[] = [];

      for (const newlyAddedId of newlyAddedIds) {
        const newContract = choices.find((entry) => entry.id === newlyAddedId);
        if (!newContract) {
          continue;
        }

        if (firstContract && newlyAddedId !== previousSelectedIds[0]) {
          const isCompatible = CAMPAIGN_PROCESSING_KEYS.every(
            (key) => newContract.campaigns[key] === firstContract.campaigns[key],
          );

          if (!isCompatible) {
            errorMessage =
              'resources.bo_common.b2b.campaigns.sections.general.errors.invalid_contracts';
            rejectedIds.push(newlyAddedId);
            continue;
          }
        }

        if (
          campaignType === 'voluntary_deposit' &&
          isInvalidVoluntaryDepositContract(newContract)
        ) {
          errorMessage =
            'resources.bo_common.b2b.campaigns.sections.general.errors.invalid_contracts_voluntary_deposit';
          rejectedIds.push(newlyAddedId);
        }
      }

      const validIds = newSelectedIds.filter((id) => !rejectedIds.includes(id));

      if (errorMessage) {
        notify(errorMessage, { autoHideDuration: 5000, type: 'error' });
        setValue(source, validIds, { shouldValidate: true });
        previousSelectedIdsRef.current = validIds;
        return;
      }

      previousSelectedIdsRef.current = newSelectedIds;
    },
    [campaignType, choices, notify, setValue, source],
  );

  useValueChanged(campaignType, () => {
    if (campaignType === 'voluntary_deposit') {
      const currentSelectedIds = getValues(source) || [];
      const invalidContractIds = choices
        .filter(
          (contract) =>
            currentSelectedIds.includes(contract.id) && isInvalidVoluntaryDepositContract(contract),
        )
        .map((contract) => contract.id as number);

      if (invalidContractIds.length > 0) {
        notify(
          'resources.bo_common.b2b.campaigns.sections.general.errors.invalid_contracts_voluntary_deposit_removed',
          { autoHideDuration: 5000, type: 'warning' },
        );
        const validIds = currentSelectedIds.filter(
          (id: number) => !invalidContractIds.includes(id),
        );
        setValue(source, validIds, { shouldValidate: true });
        previousSelectedIdsRef.current = validIds;
      }
    }
  });

  return (
    <AutocompleteArrayInput
      source={source}
      choices={choices}
      loading={isLoading}
      matchSuggestion={matchSuggestion}
      optionText={optionText}
      isRequired={isRequired}
      validate={validate}
      onChange={handleChange}
      {...props}
      data-testid={testId}
    />
  );
};
